import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { resolveMetadata } from "./metadata";

const Context = createContext(null);
const staticSelector =
  'title,meta[name="title"],meta[name="description"],meta[name="keywords"],meta[name="robots"],meta[name="googlebot"],meta[name^="twitter:"],meta[property^="og:"],meta[name^="geo."],meta[name="author"],meta[name="publisher"],meta[name="copyright"],meta[name="DC.title"],meta[name="apple-mobile-web-app-title"],link[rel="canonical"],link[hreflang],script[type="application/ld+json"]';
export function HelmetProvider({ children }) {
  const manager = useMemo(() => {
    const entries = new Map();
    let order = 0, queued = false, initialized = false;
    const sync = () => {
      if (queued) return;
      queued = true;
      queueMicrotask(() => {
        queued = false;
        if (!initialized && entries.size) {
          document.head.querySelectorAll(staticSelector).forEach((node) =>
            node.remove()
          );
          initialized = true;
        }
        document.head.querySelectorAll("[data-managed-seo]").forEach((node) =>
          node.remove()
        );
        for (const descriptor of resolveMetadata([...entries.values()])) {
          const node = document.createElement(descriptor.tag);
          for (const [key, value] of Object.entries(descriptor.attrs || {})) {
            if (value != null && value !== false) {
              node.setAttribute(
                ({
                  httpEquiv: "http-equiv",
                  hrefLang: "hreflang",
                  crossOrigin: "crossorigin",
                  charSet: "charset",
                })[key] || key,
                String(value),
              );
            }
          }
          node.textContent = descriptor.text || "";
          node.setAttribute("data-managed-seo", "");
          document.head.appendChild(node);
        }
      });
    };
    return {
      register(id, entry) {
        entries.set(id, { ...entry, order: entries.get(id)?.order ?? ++order });
        sync();
      },
      remove(id) {
        entries.delete(id);
        sync();
      },
    };
  }, []);
  return <Context.Provider value={manager}>{children}</Context.Provider>;
}
function descriptors(children) {
  const result = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) {
      result.push(...descriptors(child.props.children));
      return;
    }
    if (
      !["title", "meta", "link", "script", "style", "noscript"].includes(
        child.type,
      )
    ) return;
    const { children: text, dangerouslySetInnerHTML, ...attrs } = child.props;
    result.push({
      tag: child.type,
      attrs,
      text: dangerouslySetInnerHTML?.__html ??
        (Array.isArray(text)
          ? text.join("")
          : text == null
          ? ""
          : String(text)),
    });
  });
  return result;
}
export function Helmet({ children, priority = 2 }) {
  const manager = useContext(Context);
  const id = useRef(Symbol("metadata"));
  const nodes = JSON.stringify(descriptors(children));
  useLayoutEffect(() => {
    manager?.register(id.current, { priority, nodes: JSON.parse(nodes) });
  }, [manager, priority, nodes]);
  useLayoutEffect(() => {
    const current = id.current;
    return () => manager?.remove(current);
  }, [manager]);
  return null;
}
